import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export interface HelpCategory {
  id: string;
  slug: string;
  title: string;
  description: string;
  icon: string;
  order: number;
}

export interface HelpArticle {
  id: string;
  category: string;
  category_slug: string;
  category_title: string;
  subcategory: string | null;
  slug: string;
  title: string;
  excerpt: string | null;
  content: string;
  search_keywords: string[];
  views_count: number;
  helpful_count: number;
  target_audience: string | null;
  order_index: number;
  created_at: string;
  updated_at: string;
}

export interface HelpSearchResult {
  id: string;
  category_slug: string;
  slug: string;
  title: string;
  excerpt: string | null;
  content_snippet: string;
  relevance: number;
}

// Query keys
export const helpCategoriesQueryKey = () => ['help', 'categories'] as const;
export const helpArticlesQueryKey = (category?: string | null) =>
  ['help', 'articles', category ?? 'all'] as const;
export const helpArticleQueryKey = (slug: string | null | undefined) =>
  ['help', 'article', slug] as const;
export const helpSearchQueryKey = (query: string) => ['help', 'search', query] as const;
export const helpPopularQueryKey = () => ['help', 'popular'] as const;
export const helpRecentQueryKey = () => ['help', 'recent'] as const;

// Category icon mapping (since we derive categories from articles)
const categoryIcons: Record<string, string> = {
  'getting-started': 'rocket',
  'account': 'user',
  'billing': 'credit-card',
  'features': 'star',
  'troubleshooting': 'wrench',
  'integrations': 'puzzle',
  'security': 'shield',
  'api': 'code',
};

// Helper to format category slug to title
function formatCategoryTitle(category: string): string {
  return category
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Helper to map raw row to HelpArticle
function mapRowToArticle(row: any): HelpArticle {
  return {
    id: row.id,
    category: row.category,
    category_slug: row.category,
    category_title: formatCategoryTitle(row.category),
    subcategory: row.subcategory ?? null,
    slug: row.slug,
    title: row.title,
    excerpt: row.excerpt ?? null,
    content: row.content,
    search_keywords: row.search_keywords ?? [],
    views_count: row.views_count ?? 0,
    helpful_count: row.helpful_count ?? 0,
    target_audience: row.target_audience ?? null,
    order_index: row.order_index ?? 0,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

// Hook: Get all categories (derived from help_articles)
export function useHelpCategories() {
  return useQuery({
    queryKey: helpCategoriesQueryKey(),
    queryFn: async (): Promise<HelpCategory[]> => {
      const { data, error } = await supabase
        .from('help_articles')
        .select('category')
        .eq('is_published', true);

      if (error) throw error;

      // Extract unique categories and create category objects
      const uniqueCategories = [...new Set((data ?? []).map((row) => row.category))];

      return uniqueCategories.map((category, index) => ({
        id: category,
        slug: category,
        title: formatCategoryTitle(category),
        description: '',
        icon: categoryIcons[category] ?? 'folder',
        order: index,
      }));
    },
    staleTime: 300_000, // 5 minutes
  });
}

// Hook: Get all articles (with optional category filter)
export function useHelpArticles(category?: string | null) {
  return useQuery({
    queryKey: helpArticlesQueryKey(category),
    queryFn: async (): Promise<HelpArticle[]> => {
      let query = supabase
        .from('help_articles')
        .select('*')
        .eq('is_published', true)
        .order('order_index', { ascending: true });

      if (category) {
        query = query.eq('category', category);
      }

      const { data, error } = await query;
      if (error) throw error;

      return (data ?? []).map(mapRowToArticle);
    },
    staleTime: 300_000,
  });
}

// Hook: Get single article by slug
export function useHelpArticle(categorySlug: string | undefined, articleSlug: string | undefined) {
  return useQuery({
    queryKey: helpArticleQueryKey(articleSlug),
    enabled: Boolean(categorySlug && articleSlug),
    queryFn: async (): Promise<HelpArticle | null> => {
      if (!categorySlug || !articleSlug) return null;

      const { data, error } = await supabase
        .from('help_articles')
        .select('*')
        .eq('slug', articleSlug)
        .eq('category', categorySlug)
        .eq('is_published', true)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return null; // No rows found
        throw error;
      }

      // Note: increment_help_article_views RPC doesn't exist
      // View tracking is handled client-side via trackHelpArticleView

      return mapRowToArticle(data);
    },
    staleTime: 300_000,
  });
}

// Hook: Search articles
export function useHelpSearch(query: string) {
  const trimmedQuery = query.trim();

  return useQuery({
    queryKey: helpSearchQueryKey(trimmedQuery),
    enabled: trimmedQuery.length >= 2,
    queryFn: async (): Promise<HelpSearchResult[]> => {
      if (trimmedQuery.length < 2) return [];

      // Use ILIKE for search (simpler approach without search_tsv)
      const pattern = `%${trimmedQuery}%`;
      const { data, error } = await supabase
        .from('help_articles')
        .select('id, slug, title, excerpt, content, category')
        .eq('is_published', true)
        .or(`title.ilike.${pattern},excerpt.ilike.${pattern},content.ilike.${pattern}`)
        .limit(10);

      if (error) throw error;

      return (data ?? []).map((row: any, idx: number) => ({
        id: row.id,
        category_slug: row.category,
        slug: row.slug,
        title: row.title,
        excerpt: row.excerpt ?? null,
        content_snippet: row.content ? row.content.substring(0, 150) + '...' : '',
        relevance: 1 - idx * 0.05,
      }));
    },
    staleTime: 60_000,
  });
}

// Hook: Get popular articles (by views_count)
export function useHelpPopular(limit: number = 5) {
  return useQuery({
    queryKey: helpPopularQueryKey(),
    queryFn: async (): Promise<HelpArticle[]> => {
      const { data, error } = await supabase
        .from('help_articles')
        .select('*')
        .eq('is_published', true)
        .order('views_count', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return (data ?? []).map(mapRowToArticle);
    },
    staleTime: 300_000,
  });
}

// Hook: Get recently viewed articles from localStorage
export function useHelpRecent(limit: number = 3) {
  return useQuery({
    queryKey: helpRecentQueryKey(),
    queryFn: async (): Promise<HelpArticle[]> => {
      const recentStr = localStorage.getItem('help_recent_articles');
      if (!recentStr) return [];

      const recentIds: string[] = JSON.parse(recentStr);
      if (recentIds.length === 0) return [];

      const { data, error } = await supabase
        .from('help_articles')
        .select('*')
        .in('id', recentIds.slice(0, limit))
        .eq('is_published', true);

      if (error) throw error;

      // Sort by order in recentIds
      const sorted = (data ?? []).sort((a, b) => {
        return recentIds.indexOf(a.id) - recentIds.indexOf(b.id);
      });

      return sorted.map(mapRowToArticle);
    },
    staleTime: 60_000,
  });
}

// Helper: Track article view in localStorage
export function trackHelpArticleView(articleId: string) {
  const recentStr = localStorage.getItem('help_recent_articles');
  const recent: string[] = recentStr ? JSON.parse(recentStr) : [];

  // Remove if already exists
  const filtered = recent.filter((id) => id !== articleId);

  // Add to front
  const updated = [articleId, ...filtered].slice(0, 10);

  localStorage.setItem('help_recent_articles', JSON.stringify(updated));
}
